import {
  ACTIVE_FILTER_DELIMITER,
  Badge,
  FeaturedIcon,
  GlossaryData,
  GlossaryNavigator,
  HeroBackground,
  IFilter,
  LiveTextBox,
  useFiltersAndSort,
  useFuzzySearch,
  useWindowHeight,
  useWindowWidth,
} from '@prishapolicy/shared/ui';
import { CallbackForm, GlossaryModal, GlossaryCard } from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import { useState, useRef } from 'react';
import glossaryData from './glossaryWords.json';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';

const data: Array<GlossaryData> = glossaryData;

const InsuranceGlossary = (props: { recentSearches: Array<GlossaryData> }) => {
  const { isLg, isXl } = useWindowWidth();
  const { height } = useWindowHeight();
  const glossaryRef = useRef(null);
  const [query, setQuery] = useState('');
  const alphabets = 'abcdefghijklmnopqrstuvwxyz'.split('');

  const filters: IFilter<GlossaryData>[] = [
    {
      groupName: 'alphabet',
      allowedMultipleFilters: false,
      filterGroup: alphabets.map((alphabet) => {
        return {
          displayName: alphabet,
          value: alphabet,
          filterFunction: (glossarData: GlossaryData) => glossarData.englishTitle.startsWith(alphabet.toUpperCase()),
        };
      }),
    },
  ];

  const filtersAndSort = useFiltersAndSort({
    data,
    filters,
  });

  const finalData = useFuzzySearch(filtersAndSort.filteredData, query);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const executeScroll = () =>
    glossaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start', offsetTop: 100 });
  return (
    <>
      <NextSeo
        title="Glossary | PrishaPolicy"
        description="Insurance Glossary - Access, learn and discover all terms related to insurance here. Now also in Hindi"
      />
      <HeroBackground backgroundColor="yellow-100">
        <div className="flex flex-col items-stretch text-center">
          <h1 className="font-bold" style={{ fontSize: isLg ? 60 : 40 }}>
            Insurance {!isLg ? <br /> : ''}Glossary
          </h1>
          <h3 className="lg:text-title-5 text-title-6 mt-5 grid max-w-full place-items-center font-medium">
            Access, learn and discover all terms related to insurance here. Now also in Hindi
          </h3>
          <div className="justify-center lg:flex">
            <div style={{ width: isLg ? '800px' : '' }} className="mt-9 flex items-center gap-4">
              <LiveTextBox value={query} setValue={setQuery} className=" flex-1 bg-white" />
              {(!isXl || height < 700) && (
                <FeaturedIcon
                  icon={SearchLineIcon}
                  theme="solid"
                  type="square"
                  className={query !== '' && 'cursor-pointer'}
                  onClick={query !== '' ? executeScroll : null}
                />
              )}
            </div>
          </div>
          {
            <div className="mt-6 grid place-items-center">
              <div>
                <div className="text-title-6 grid place-items-start font-medium">Recent Searches</div>
                <div
                  className="mt-2 flex flex-wrap place-items-center gap-2 lg:mt-4 lg:justify-center"
                  style={{ minHeight: '40px' }}
                >
                  {props.recentSearches.map((arrElement, idx) => {
                    return (
                      <div
                        key={idx}
                        className="cursor-pointer"
                        onClick={() => {
                          setQuery(arrElement.englishTitle);
                          (!isXl || height < 750) && executeScroll();
                        }}
                      >
                        <Badge text={arrElement.englishTitle} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          }
        </div>
      </HeroBackground>
      <div className="lg:mt-15 container mt-10" ref={glossaryRef}>
        <div className="flex flex-col xl:grid xl:grid-cols-4 xl:gap-20 " style={{ maxWidth: '1800px' }}>
          <div className="col-span-1 grid h-fit gap-4">
            <GlossaryNavigator {...filtersAndSort} />
            <div className="hidden lg:block">
              <CallbackForm />
            </div>
          </div>
          <div className="mt-6 lg:col-span-3 lg:mt-0">
            <div>
              {filtersAndSort.activeFilters.size === 0 ? (
                `Showing ${filtersAndSort.filteredData.length} Results`
              ) : (
                <div>
                  {finalData.length > 0 ? (
                    <div>
                      {`Showing ${finalData.length} words starting from `}
                      <span className="text-body-1 text-primary font-bold">
                        &quot;
                        {[...filtersAndSort.activeFilters][0].split(ACTIVE_FILTER_DELIMITER)[1].toUpperCase()}
                        &quot;
                      </span>
                    </div>
                  ) : (
                    <div>No Results for selected Letter</div>
                  )}
                </div>
              )}
            </div>
            <div>
              <GlossaryModal isOpen={isOpen} setIsOpen={setIsOpen} content={finalData[activeIndex]} />
              <div className="mt-4 flex flex-col gap-2 lg:mt-6 lg:grid lg:grid-cols-2 lg:gap-4">
                {finalData.map((arrElement, idx) => {
                  return (
                    <GlossaryCard
                      key={idx}
                      idx={idx}
                      title={arrElement.englishTitle}
                      englishContent={arrElement.englishContent}
                      setIsOpen={setIsOpen}
                      setActiveIndex={setActiveIndex}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps = () => {
  const recentSearches = [
    {
      englishTitle: 'Sum Insured',
    },
    {
      englishTitle: 'Insured',
    },
    {
      englishTitle: 'Corporate Floater',
    },
    {
      englishTitle: 'Aggregate Deductibles',
    },
    {
      englishTitle: 'Act of God perils',
    },
    {
      englishTitle: 'Endorsement',
    },
    {
      englishTitle: 'Moral Hazard',
    },
    {
      englishTitle: 'Grace Period',
    },
    {
      englishTitle: 'Financial Underwriting',
    },
    {
      englishTitle: 'Co - Payment',
    },
  ];

  const randomizedRecentSearches = recentSearches
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value)
    .slice(0, 6);

  return {
    props: {
      recentSearches: randomizedRecentSearches,
    },
  };
};
export default InsuranceGlossary;
